import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import Link from "@mui/material/Link";
import Checkbox from "@mui/material/Checkbox";
// @ts-ignore
import { useTheme } from "@mui/material/styles";
// helpers
import { navigateBackOrToMain } from "../../../../common/utils/helpers/navigateBackOrToMain";
import { shouldShowMilesAndMore } from "../../../../common/utils/transport/airlineBookingPayload";
// hooks
import { useActions } from "../../../../common/hooks/useActions";
// actions
import { sbbActionCreators } from "../sbbModule";
import { errorFieldActionCreators } from "../../../../common/store/modules/errorFieldModule";
// components
import AppFormInput from "../../../../components/form/AppFormInputs/AppFormInput";
import AppButton from "../../../../components/ui/AppButton";
import MilesAndMoreInfo from "../../../../components/MilesAndMoreInfo";
import TransportCancellationPolicy from "../../../../components/TransportProviderContent/TransportCancellationPolicy";

interface SbbSummaryTypes {
  onBuyTicket: () => void;
  isZurichCard?: boolean;
  cancellationPolicy?: string | null;
}

export default function SbbSummary({ onBuyTicket, isZurichCard, cancellationPolicy }: SbbSummaryTypes) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const language = localStorage.getItem("i18nextLng") || "en";

  // states
  const tenantInfo = useSelector((state: any) => state.tenant.tenantInfo);
  const prebookPriceInfo = useSelector((state: any) => state.sbb.prebookPriceInfo);
  const dataForBooking = useSelector((state: any) => state.sbb.dataForBooking);
  const errorField = useSelector((state: any) => state.errorField.errorField);
  const sbbActiveOffer = useSelector((state: any) => state.sbb.sbbActiveOffer);
  const platformType = useSelector((state: any) => state.global.platformType);
  // actions
  const updateFormForBooking = useActions(sbbActionCreators?.updateFormForBookingAction, []);
  const resetPrebookDataInfo = useActions(sbbActionCreators?.resetPrebookDataInfoAction, []);
  const resetErrorField = useActions(errorFieldActionCreators?.resetErrorFieldAction, []);

  // helpers
  const isAirline = platformType === "airline";

  useEffect(() => {
    return () => {
      // reset error field on unmount summary component
      resetErrorField();
    };
  }, [resetErrorField]);

  const handleGuestEmail = (e: any) => {
    updateFormForBooking({
      ...dataForBooking,
      emailForm: e.target.value
    });
  };

  const handleComment = (e: any) => {
    updateFormForBooking({
      ...dataForBooking,
      commentForm: e.target.value
    });
  };

  const handleTerms = (e: any) => {
    updateFormForBooking({
      ...dataForBooking,
      acceptTerms: e.target.checked
    });
  };

  const handeEditTicketOptionButton = () => {
    if (window.confirm(t("transport.editOptionText"))) {
      resetPrebookDataInfo();
    }
  };

  const getSummaryRouteTitle = (): string => {
    if (sbbActiveOffer?.code) {
      return sbbActiveOffer?.name;
    } else {
      return `${prebookPriceInfo?.details?.departure?.name} - ${prebookPriceInfo?.details?.destination?.name}`;
    }
  };

  return (
    prebookPriceInfo && (
      <Box sx={{ display: "flex", flexDirection: "column" }} pb={3} pt={3}>
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between" }}>
            <Box>
              <Box className="summary-option-title" mb={2}>
                {t("transport.ticketOption")}
              </Box>
              <Box className="summary-option-title default-text-transform">{getSummaryRouteTitle()}</Box>
            </Box>
            <Grid item sx={{ width: "auto", height: "40px" }} mt={{ xs: 1.5, sm: 0 }}>
              <AppButton
                label={t("transport.editBtn")}
                onClick={handeEditTicketOptionButton}
                disableRipple={true}
                customBackgroundColor={theme.palette.primary.dark}
                customHoverColor={theme.palette.primary.dark}
                fontSize="14px"
              />
            </Grid>
          </Box>
          <Grid container className="summary-passenger-box" mt={2} mb={2} pb={2} pt={2}>
            {prebookPriceInfo?.details?.passengers?.length > 0 &&
              prebookPriceInfo?.details?.passengers?.map((passenger: any) => {
                return (
                  <Box
                    key={Math.random()}
                    sx={{ display: "flex", textAlign: "center", alignItems: "center", justifyContent: "center" }}
                  >
                    <Grid item xs={3} pl={{ xs: 1, sm: 0 }}>
                      {passenger?.first_name} {passenger?.last_name}
                    </Grid>
                    <Grid item xs={3}>
                      {format(new Date(passenger?.birthdate), "dd.MM.yyyy")}
                    </Grid>
                    {!isZurichCard && (
                      <Grid item xs={3}>
                        {passenger?.member_card ? "half-fare" : "full-fare"}
                      </Grid>
                    )}
                    <Grid item xs={3}>
                      {passenger?.price} {passenger?.currency}
                    </Grid>
                  </Box>
                );
              })}
          </Grid>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center" }} />
            <Box className="summary-total-price-label" mr={{ xs: 0, sm: 3, md: 6 }}>
              {t("transport.totalPrice")}: {prebookPriceInfo?.formatted_price}
            </Box>
          </Box>
        </Box>
        <Grid container mt={2}>
          {/* Left side: email + comment */}
          <Grid item xs={12} md={6}>
            <Grid container width={{ sm: "100%", md: "100%" }}>
              <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
                <Grid item xs={3}>
                  <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>{t("transport.guestEmailLabel")} *</Box>
                </Grid>
                <Grid item xs={9} ml={1}>
                  <AppFormInput
                    name={"emailForm"}
                    type="string"
                    value={dataForBooking?.emailForm}
                    onChange={handleGuestEmail}
                    error={errorField?.emailForm}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
                <Grid item xs={3}>
                  <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>{t("transport.commentLabel")}</Box>
                </Grid>
                <Grid item xs={9} ml={1}>
                  <AppFormInput
                    name={"commentForm"}
                    type="string"
                    value={dataForBooking?.commentForm}
                    onChange={handleComment}
                    error={errorField?.commentForm}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Right side: Miles & More info (responsive: below on mobile) */}
          {shouldShowMilesAndMore(prebookPriceInfo?.miles_and_more, isAirline) && (
            <Grid
              item
              xs={12}
              md={6}
              pl={{ xs: 0, md: 5 }}
              pt={{ xs: 4, md: 0 }}
              sx={{ alignItems: "center", display: "flex" }}
            >
              <MilesAndMoreInfo miles={prebookPriceInfo?.miles_and_more} />
            </Grid>
          )}
        </Grid>
        {isZurichCard && prebookPriceInfo?.details?.terms_and_conditions?.length > 0 && (
          <Grid item xs={12} mt={3}>
            <Grid item style={{ display: "flex", alignItems: "flex-start", flexDirection: "column" }}>
              <Box style={{ flex: 1 }}>
                <Checkbox
                  sx={{
                    "&&.PrivateSwitchBase-root": {
                      paddingLeft: 0
                    }
                  }}
                  checked={dataForBooking?.acceptTerms}
                  onChange={event => handleTerms(event)}
                />{" "}
                {/* TODO: text will be updated and translated, after business decision */} I accept terms and conditions
              </Box>
              <Box
                sx={{ fontSize: { xs: 12, sm: 14 } }}
                style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "flex-start" }}
              >
                {prebookPriceInfo?.details?.terms_and_conditions?.map((term: any, index: number) => (
                  <Link
                    key={index}
                    target="_blank"
                    rel="noreferrer"
                    underline="hover"
                    sx={{
                      fontSize: { xs: 12, sm: 14 },
                      color: "#76a8cf",
                      marginLeft: 1,
                      textDecoration: "underline",
                      marginTop: index !== 0 ? 1 : 0
                    }}
                    href={term?.url}
                  >
                    {term?.name}
                  </Link>
                ))}
              </Box>
            </Grid>
          </Grid>
        )}

        {isZurichCard && errorField?.acceptTerms && (
          <Box mt={1} className="uber__error-msg">
            {/* TODO: text will be updated and translated, after business decision */}
            You must accept the terms to book the offer
          </Box>
        )}
        {cancellationPolicy && (
          <Box pt={6}>
            <TransportCancellationPolicy cancellationPolicy={cancellationPolicy} />
          </Box>
        )}
        <Grid
          item
          xs={12}
          sx={{
            width: { xs: "100%", sm: "100%", md: isAirline ? "45%" : "30%" },
            margin: cancellationPolicy?.trim() ? "24px auto 0 auto" : "60px auto 0 auto",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 2 }
          }}
        >
          {isAirline && (
            <AppButton
              label={t("transport.goBack")}
              onClick={() => navigateBackOrToMain(navigate, { language })}
              disableRipple={true}
              customBackgroundColor={theme.palette.primary.dark}
              customHoverColor={theme.palette.primary.dark}
              fontSize="14px"
            />
          )}
          <AppButton
            label={t("transport.buyTicketsBtn")}
            onClick={onBuyTicket}
            disableRipple={true}
            customBackgroundColor={tenantInfo?.style?.button?.color}
            customActiveColor={tenantInfo?.style?.button?.active}
            customHoverColor={tenantInfo?.style?.button?.hover}
            customClickColor={tenantInfo?.style?.button?.click}
            fontSize="14px"
          />
        </Grid>
      </Box>
    )
  );
}
